import {
  InputFieldsType,
  designTypeFields,
  orderedFields,
} from '@composites/InboundButton/utils';
import {
  ACCOUNT_ID,
  usePublicAttendees,
  usePublicPortfolioByAccount,
  usePublicEventDetails,
} from '@utils/apiHooks';
import { printPdf, useMobileWidth } from '@utils/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Button from '@components/Button';

import { DefaultBooleanValues } from '@composites/InboundButton';
import AttendeeList from '@pages/AttendeeList';
import { getEventDetails } from '@store/slices/eventSlice';
import { safeJSONParse, getImageSrc, getUrl } from '@utils/fn';
import { useInbound } from '@utils/Inbound/useInbound';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import Spinner from '@components/Spinner';
import { useSelector } from 'react-redux';

interface DownloadAttendeeListProps {
  label: string;
  buttonType: string;
  onClick?: () => void;
  eventId?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
  pastMode?: boolean;
  downloadEventIdOverride?: string;
}

const DownloadAttendeeList = ({
  label,
  onClick,
  buttonType,
  eventId,
  inboundValues,
  pastMode,
  downloadEventIdOverride,
}: DownloadAttendeeListProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [showModal, setShowModal] = useState<boolean>(false);
  const [checkedApproval, setCheckedApproval] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const isMobile = useMobileWidth();
  const { inboundValue, actionInbound, registerInbound, shouldShowForm } =
    useInbound();

  const effectiveDownloadEventId = pastMode
    ? downloadEventIdOverride || ''
    : sluggedEvent?.id || '';

  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  const logoUrl = useMemo(() => {
    const portfolio = portfolios?.data?.find(
      p => p?.id === sluggedEvent?.portfolio?.id
    );
    const d = (portfolio?.design ?? {}) as any;

    if (d?.logoPosImagePng) return getImageSrc(d.logoPosImagePng);
    if (d?.logoNegImagePng) return getImageSrc(d.logoNegImagePng);

    if (sluggedEvent?.design?.eventLogoPosSvg)
      return getImageSrc(sluggedEvent.design.eventLogoPosSvg);

    return 'https://media.we-conect.com/images/website/smart_mobility_neg.png';
  }, [portfolios, sluggedEvent]);

  const isPast = !!pastMode;

  const getColocatedFlag = (d: any) =>
    Boolean(
      d?.data?.event?.colocated ??
        d?.event?.colocated ??
        d?.result?.data?.event?.colocated ??
        d?.result?.data?.colocated ??
        d?.data?.colocated ??
        d?.colocated
    );

  const { data: pastDetails, isFetched: pastFetched } = usePublicEventDetails({
    eventId: effectiveDownloadEventId,
  });

  const pastColocated = getColocatedFlag(pastDetails);

  const colocatedForDownload = isPast
    ? pastColocated
    : !!sluggedEvent?.colocated;
  const enableDownloadAttendees =
    effectiveDownloadEventId.length > 0 && (!isPast || pastFetched);

  const { data } = usePublicAttendees({
    eventId: effectiveDownloadEventId,
    enabled: enableDownloadAttendees,
    colocated: colocatedForDownload,
  });

  const account = useMemo(
    () => portfolios?.data?.[0]?.account,
    [portfolios?.data]
  );

  const generateAttendeeListPDF = useCallback(() => {
    const filteredAttendees = (data?.attendees ?? []).filter(
      attendee => String(attendee?.company?.name ?? '').trim().length > 0
    );

    const pdfBaseName = i18n.language?.startsWith('de')
      ? pastMode
        ? 'Vorjahres Teilnehmerliste'
        : t('attendee-list')
      : pastMode
        ? 'Past Attendee List'
        : t('attendee-list');

    const fileName = `${pdfBaseName} - ${sluggedEvent?.name}`;

    const doc = (
      <AttendeeList
        event={sluggedEvent}
        theme={theme}
        account={account}
        portfolios={portfolios}
        attendees={filteredAttendees}
        logoUrl={logoUrl}
        pastMode={pastMode}
      />
    );
    setLoading(true);
    printPdf(doc, fileName)
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  }, [
    account,
    data?.attendees,
    sluggedEvent,
    t,
    theme,
    i18n.language,
    pastMode,
    logoUrl,
  ]);

  const inputElements = useMemo(
    () =>
      orderedFields
        .map(item => {
          if (inboundValues?.booleanValues?.[item]?.visible) {
            const designTypeParameters = designTypeFields?.find(
              e => e.id === item
            );
            return {
              ...designTypeParameters,
              ...inboundValues?.booleanValues?.[item],
            };
          }
          return undefined;
        })
        ?.filter(item => item) as InputFieldsType,
    [inboundValues?.booleanValues]
  );

  const defaultValues: Record<string, string> = useMemo(() => {
    return inputElements.reduce<Record<string, string>>((acc, key) => {
      acc[key.id] = safeJSONParse(inboundValue)?.[key.id] ?? '';
      return acc;
    }, {});
  }, [inboundValue, inputElements]);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Clear error for a field when user starts typing in it
  const handleChange = (fieldName: string) => (value: string) => {
    clearErrors(fieldName);
  };

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      if (!checkedApproval) return;

      await registerInbound(
        {
          ...data,
          event: sluggedEvent?.id,
          path: window.location.href,
          action: 'download_attendee_list',
        },
        () => {
          generateAttendeeListPDF();
          setShowModal(false);
        }
      );
    },
    [
      checkedApproval,
      inboundValue,
      registerInbound,
      sluggedEvent?.id,
      generateAttendeeListPDF,
    ]
  );
  return (
    <>
      <Button
        icon={loading ? <Spinner size="2rem" /> : undefined}
        title={label}
        backgroundColor={sluggedEvent?.design?.themeColor}
        hoverBackgroundColor={theme.color.mobilityGrey}
        textColor={theme.color.white}
        lineHeight={theme.lineHeight.height125}
        fontSize={theme.fontSize.size20}
        borderRadius={theme.borderRadius.radius10}
        padding="1.1rem 3rem"
        width={isMobile ? '100%' : 'max-content'}
        disabled={loading}
        onClick={() => {
          void (async () => {
            if (shouldShowForm) {
              // if Past redirect to past
              const regPath = pastMode
                ? t('past-attendee-list-reg-url')
                : t('attendee-list-reg-url');
              const href = `${window.location.origin}/events/${sluggedEvent?.slug}${regPath}`;
              window.location.href = href;
              return;
            }
            // Has inbound value => download + inbound
            generateAttendeeListPDF();
            await actionInbound({
              ...JSON.parse(inboundValue ?? '{}'),
              event: sluggedEvent?.id, // <— current event
              path: window.location.href,
              action: 'download_attendee_list',
            });
          })();
        }}
      />
    </>
  );
};

export default DownloadAttendeeList;
